import React, { useContext, useState, useEffect } from 'react';
import { Container, TextField, Button, Typography, Alert, Paper, Box, CircularProgress } from '@mui/material';
import userApi from '../../api/userApi';
import { AuthContext } from '../../auth/AuthContext';
import { useNavigate } from 'react-router-dom';
import logoImg from '../../assets/logo.png';
import InstallPWAButton from '../../components/InstallPWAButton';
import { useMediaQuery } from '@mui/material';

export default function LoginPage() {

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [otpRequired, setOtpRequired] = useState(false);
  const isMobile = useMediaQuery('(max-width:768px)');
  const [err, setErr] = useState(null);
  const [info, setInfo] = useState(null);
  const { login } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const APP_VERSION = process.env.REACT_APP_VERSION;

  // ✅ Ripristino stato al mount
  useEffect(() => {
    const savedState = sessionStorage.getItem('loginState');
    if (savedState) {
      const parsed = JSON.parse(savedState);
      setUsername(parsed.username || '');
      setPassword(parsed.password || '');
      setOtp(parsed.otp || '');
      setOtpRequired(parsed.otpRequired || false);
      setInfo(parsed.info || null);
    }
  }, []);

  // ✅ Salvataggio automatico stato
  useEffect(() => {
    const state = {
      username,
      password,
      otp,
      otpRequired,
      info
    };
    sessionStorage.setItem('loginState', JSON.stringify(state));
  }, [username, password, otp, otpRequired, info]);

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);

    try {
      setLoading(true);

      if (!otpRequired) {
        const res = await userApi.login({ username, password, otp });
        console.log("dati login", res.data);

        if (res.data.otpRequired) {
          setOtpRequired(true);
          setInfo('Inserisci il codice OTP ricevuto via email e clicca sul bottone VERIFICA OTP');
        } else {
          loginUser(res.data);
        }
      } else {
        const res = await userApi.login({ username, password, otp });
        console.log("dati OTP", res.data);
        loginUser(res.data);
      }

    } catch (e) {
      setErr(e.response?.data || 'Credenziali non valide');
    } finally {
      setLoading(false);
    }
  };

  const loginUser = (data) => {

    // ✅ Pulizia stato salvato dopo login riuscito
    sessionStorage.removeItem('loginState');

    login({
      token: data.token,
      user: {
        username: data.username,
        nomecognomeutente: data.nomecognomeutente,
        role: data.profilo,
        studioId: data.studioId,
        codiceInvito: data.codiceInvito,
        contribuenteId: data.contribuenteId,
        userId: data.id,
        enabled: data.enabled,
        tipoContratto: data.tipoContratto,
        statoContratto: data.statoContratto,
        contrattoAttivo: data.contrattoAttivo,
        giorniResidui: data.giorniResidui,
        contrattoId: data.contrattoId,
        googleCalendarEnabled: data.googleCalendarEnabled,
        stripeenabled: data.stripeenabled,
        calendarEnabled: data.calendarEnabled,
        chatisactive: data.chatisactive,
      }
    });

    if (data.profilo === "STUDIO") navigate("/dashboard/studio/" + data.studioId);
    else if (data.profilo === "CONTRIBUENTE") navigate("/dashboard/contribuente/" + data.contribuenteId);
    else if (data.profilo === "ADMIN") navigate("/dashboard");
  };

  return (
    <Container maxWidth="xs" sx={{ mt: 10 }}>
      <Paper elevation={6} sx={{ p: 4, borderRadius: 3, textAlign: 'center' }}>
        <Box sx={{ mb: 2 }}>
          <img src={logoImg} alt="TaxFlow" style={{ maxWidth: '150px' }} />
        </Box>

        <Typography variant="h4" sx={{ mb: 1, fontWeight: 'bold', color: 'primary.main' }}>
          DeskStudio
        </Typography>

        <Typography sx={{ mb: 3, color: 'text.secondary' }}>
          Gestione Studi · Contribuenti · Documenti
          <Typography variant="caption" sx={{ mt: 3, display: 'block', color: 'text.secondary' }}>
            Versione {APP_VERSION}
          </Typography>
          <Typography variant="caption" sx={{ mt: 3, display: 'block', color: 'text.secondary' }}>
            <InstallPWAButton />
          </Typography>
        </Typography>

        {err && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {String(err)}
          </Alert>
        )}

        {info && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {String(info)}
          </Alert>
        )}

        {loading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0, left: 0, right: 0, bottom: 0,
              bgcolor: 'rgba(255,255,255,0.7)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10,
            }}
          >
            <CircularProgress />
          </Box>
        )}

        <form onSubmit={submit}>

          {!otpRequired && (
            <>
              <TextField
                fullWidth
                sx={{ my: 1 }}
                label="Username"
                value={username}
                onChange={e => setUsername(e.target.value)}
              />

              <TextField
                fullWidth
                sx={{ my: 1 }}
                type="password"
                label="Password"
                value={password}
                onChange={e => setPassword(e.target.value)}
              />
            </>
          )}

          {otpRequired && (
            <TextField
              fullWidth
              sx={{ my: 1 }}
              label="Codice OTP"
              value={otp}
              onChange={e => setOtp(e.target.value)}
            />
          )}

          <Button
            fullWidth
            variant="contained"
            type="submit"
            sx={{
              mt: 2,
              py: 1.2,
              fontSize: '1rem',
              borderRadius: 2,
              transition: '0.2s',
              '&:hover': { transform: 'scale(1.02)' }
            }}
          >
            {otpRequired ? 'Verifica OTP' : 'Accedi'}
          </Button>

          <Button
            fullWidth
            variant="contained"
            sx={{
              mt: 2,
              py: 1.2,
              fontSize: '1rem',
              borderRadius: 2,
              transition: '0.2s',
              '&:hover': { transform: 'scale(1.02)' }
            }}
            onClick={() => navigate('/forgot-password')}
          >
            Password dimenticata?
          </Button>

          {!otpRequired && (
            <Button
              fullWidth
              variant="contained"
              sx={{
                mt: 2,
                py: 1.2,
                fontSize: '1rem',
                borderRadius: 2,
                transition: '0.2s',
                '&:hover': { transform: 'scale(1.02)' }
              }}
              onClick={() =>
                window.open(`${process.env.PUBLIC_URL}/manualeUtente.pdf`, '_blank')
              }
            >
              Manuale utente
            </Button>
          )}

        </form>
      </Paper>
    </Container>
  );
}
